import { Star, ExternalLink, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { REVIEWS, BRAND } from '@/data/mockData';
import ParticlesBg from '@/components/common/ParticlesBg';

const ReviewCard = ({ r, source }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.4 }}
    className="glass rounded-2xl p-6 hover:border-brand-cyan/30 transition relative overflow-hidden h-full flex flex-col"
  >
    <Quote className="absolute top-4 right-4 w-8 h-8 text-brand-cyan/20" />
    <div className="flex items-center gap-1 text-yellow-400">
      {[...Array(r.stars)].map((_, i) => <Star key={i} className="w-3.5 h-3.5" fill="currentColor" />)}
    </div>
    <p className="mt-4 text-sm text-white/80 leading-relaxed flex-1">&ldquo;{r.text}&rdquo;</p>
    <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
      <div>
        <div className="font-semibold text-sm">{r.name}</div>
        <div className="text-[11px] text-brand-cyan/70 font-mono uppercase tracking-wider">{r.role}</div>
      </div>
      <div className="text-[10px] text-white/40 font-mono">{r.date} · {source}</div>
    </div>
  </motion.div>
);

const SuccessStories = () => {
  return (
    <>
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 radial-glow" />
        <ParticlesBg count={30} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono">Student Success Stories</div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-4xl font-semibold">
            Career transformations. <span className="text-brand-cyan">Verified.</span>
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl">Real reviews from UrbanPro & Trustpilot. Real careers reshaped through GenAI automation mentorship.</p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
            {[
              { v: '4.9/5', l: 'UrbanPro' },
              { v: '5.0/5', l: 'Trustpilot' },
              { v: '500+', l: 'Reviews' },
              { v: '94%', l: 'Placement' },
            ].map((s) => (
              <div key={s.l} className="glass rounded-xl p-4 text-center">
                <div className="font-display text-2xl font-black text-brand-cyan">{s.v}</div>
                <div className="text-[10px] tracking-widest uppercase font-mono text-white/60 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-3">
              UrbanPro Reviews
              <a href={BRAND.reviews.urbanproReviews} target="_blank" rel="noreferrer" className="text-sm text-brand-cyan inline-flex items-center gap-1.5 hover:underline">
                View on UrbanPro <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {REVIEWS.urbanpro.map((r) => <ReviewCard key={r.name} r={r} source="UrbanPro" />)}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-3">
              Trustpilot Reviews
              <a href={BRAND.reviews.trustpilotReviews} target="_blank" rel="noreferrer" className="text-sm text-brand-cyan inline-flex items-center gap-1.5 hover:underline">
                View on Trustpilot <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {REVIEWS.trustpilot.map((r) => <ReviewCard key={r.name} r={r} source="Trustpilot" />)}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-strong rounded-3xl p-8 sm:p-12 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold">Also see Google Reviews</h2>
            <p className="mt-2 text-white/70">Search &lsquo;BYMAT Automation&rsquo; on Google for unfiltered candid reviews.</p>
            <a href={BRAND.reviews.googleSearch} target="_blank" rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-brand-cyan text-black font-semibold px-6 py-3 rounded-lg btn-glow hover:bg-brand-cyanSoft transition">
              Open Google Search <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default SuccessStories;
